/**
 * SearchPanel - the scrollable dropdown panel content.
 *
 * Contains:
 * - Search input (300ms debounce is handled by the parent via onSearchChange)
 * - Curated local models list
 * - CivitAI results / popular models section
 */

import { memo } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { ImageModelInfo } from '@/types';
import { ModelCard } from '@/components/common/model-picker';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SearchPanelProps {
  models: ImageModelInfo[];
  civitaiModels: ImageModelInfo[];
  popularModels: ImageModelInfo[];
  selectedValue: string | undefined | null;
  searchQuery: string;
  isSearching: boolean;
  searchError: string | null;
  popularLoaded: boolean;
  brokenImages: Set<string>;
  onSearchChange: (query: string) => void;
  onSelect: (model: ImageModelInfo) => void;
  onImageBroken: (key: string) => void;
  /** Override for the main list's section header. Defaults to "Curated Models". */
  curatedSectionLabel?: string;
  /**
   * Optional second local section rendered below `models`, e.g. Deforum's
   * "Other installed" checkpoints found on disk but not part of the curated
   * catalog. Omitted entirely when not passed (Image picker has no use for it).
   */
  extraModels?: ImageModelInfo[];
  extraModelsLabel?: string;
  /** Override for the search input placeholder. Defaults to the CivitAI-search copy. */
  searchPlaceholder?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SearchPanel = memo(function SearchPanel({
  models,
  civitaiModels,
  popularModels,
  selectedValue,
  searchQuery,
  isSearching,
  searchError,
  popularLoaded,
  brokenImages,
  onSearchChange,
  onSelect,
  onImageBroken,
  curatedSectionLabel,
  extraModels,
  extraModelsLabel,
  searchPlaceholder,
}: SearchPanelProps) {
  const { t } = useTranslation();

  const displayModels = searchQuery.trim() ? civitaiModels : popularModels;
  const sectionLabel = searchQuery.trim()
    ? t('image.model.civitaiSection', { defaultValue: 'CivitAI Results' })
    : t('image.model.popularSection', { defaultValue: 'Popular on CivitAI' });
  const showCivitaiSection = searchQuery.trim() || popularModels.length > 0;

  return (
    <>
      {/* Search input */}
      <div className="p-2 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={
              searchPlaceholder ??
              t('image.model.searchPlaceholder', { defaultValue: 'Search models on CivitAI...' })
            }
            className="w-full pl-8 pr-3 py-2 min-h-[40px] text-sm bg-black/20 border border-white/10 rounded-md text-white placeholder-white/40 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30"
            autoFocus
          />
        </div>
      </div>

      {/* Scrollable list */}
      <div className="max-h-[400px] overflow-y-auto">
        {/* Curated models section header */}
        <div className="px-3 py-1.5">
          <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium">
            {curatedSectionLabel ??
              t('image.model.curatedSection', { defaultValue: 'Curated Models' })}
          </span>
        </div>

        {/* Local model cards */}
        {models.map((model) => (
          <ModelCard
            key={model.id}
            model={model}
            isSelected={selectedValue === model.id}
            brokenImages={brokenImages}
            onSelect={onSelect}
            onImageBroken={onImageBroken}
            variant="local"
          />
        ))}

        {/* Optional second local section (e.g. Deforum's "Other installed") */}
        {extraModels && extraModels.length > 0 && (
          <>
            <div className="px-3 py-1.5 mt-1 border-t border-white/5">
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium">
                {extraModelsLabel}
              </span>
            </div>
            {extraModels.map((model) => (
              <ModelCard
                key={model.id}
                model={model}
                isSelected={selectedValue === model.id}
                brokenImages={brokenImages}
                onSelect={onSelect}
                onImageBroken={onImageBroken}
                variant="local"
              />
            ))}
          </>
        )}

        {/* CivitAI section */}
        {(showCivitaiSection || isSearching) && (
          <>
            <div className="px-3 py-1.5 mt-1 border-t border-white/5">
              <span className="text-[11px] text-white/40 uppercase tracking-wider font-medium">
                {sectionLabel}
              </span>
            </div>

            {/* Loading spinner while searching */}
            {isSearching && (
              <div className="flex items-center justify-center py-4 text-white/50">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-xs">
                  {t('common.loading', { defaultValue: 'Loading...' })}
                </span>
              </div>
            )}

            {/* Loading spinner while popular models are first fetched */}
            {!isSearching && !popularLoaded && !searchQuery.trim() && (
              <div className="flex items-center justify-center py-4 text-white/50">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-xs">
                  {t('common.loading', { defaultValue: 'Loading...' })}
                </span>
              </div>
            )}

            {/* Search error */}
            {searchError && <div className="px-3 py-3 text-xs text-red-300">{searchError}</div>}

            {/* No results */}
            {!isSearching && !searchError && searchQuery.trim() && civitaiModels.length === 0 && (
              <div className="px-3 py-4 text-xs text-white/40 text-center">
                {t('image.model.noResults', { defaultValue: 'No models found' })}
              </div>
            )}

            {/* CivitAI / popular model cards */}
            {displayModels.map((model) => (
              <ModelCard
                key={model.id}
                model={model}
                isSelected={false}
                brokenImages={brokenImages}
                onSelect={onSelect}
                onImageBroken={onImageBroken}
                variant="civitai"
              />
            ))}
          </>
        )}
      </div>
    </>
  );
});

export default SearchPanel;
